import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Modal, Form } from 'react-bootstrap';
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';

import ModalHeader from '../../../common/components/ModalHeader';

const UploadImmunizationModal = ({ show, onHide, testGroup, testGroupUser }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [vaccineSearch, setVaccineSearch] = useState('');
  const [selectedVaccine, setSelectedVaccine] = useState(null);
  const [vaccinatedAt, setVaccinatedAt] = useState('');
  const [foundVaccines, setFoundVaccines] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => {
      setFiles(acceptedFiles);
    },
    accept: '.xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg',
    multiple: false
  });

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('student_upload[document]', files[0]);
    formData.append('student_upload[uploaded_by]', 'administrator');

    try {
      const response = await axios.post(`/test_groups/${testGroup.slug}/schools/student_uploads/${testGroupUser.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.data) {
        toastr.success(`Uploaded ${files[0].name}`);
        handleUploadSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toastr.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      setFiles([]);
    }
  };

  const handleUploadSuccess = () => {
    window.location.href = `/test_groups/${testGroup.slug}/schools/student_uploads/${testGroupUser.id}/review`;
  };

  const handleSaveVaccine = async () => {
    if (!selectedVaccine || !vaccinatedAt) return;

    try {
      const response = await axios.post(`/test_groups/${testGroup.slug}/immunizations/${testGroupUser.id}`, {
        vaccine_history: {
          cvx_code: selectedVaccine.cvx_code,
          vaccinated_at: vaccinatedAt,
          vaccine: selectedVaccine.name,
          source: 'upload',
          valid_dose: true,
        }
      });

      if (response.data) {
        toastr.success('Vaccine record added successfully');
        window.location.reload();
      }
    } catch (error) {
      console.error('Save error:', error);
      toastr.error('Failed to save vaccine record. Please try again.');
    }
  };

  const handleClose = () => {
    setShowManualEntry(false);
    setSelectedVaccine(null);
    setVaccineSearch('');
    setVaccinatedAt('');
    onHide();
  };

  useEffect(() => {
    const searchVaccines = async () => {
      if (vaccineSearch.length <= 2) {
        setFoundVaccines([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await axios.get(`/vaccine_searches/query`, {
          params: { query: vaccineSearch }
        });
        setFoundVaccines(response.data.vaccine_searches);
      } catch (error) {
        console.error('Search error:', error);
        toastr.error('Failed to search vaccines');
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchVaccines, 300);
    return () => clearTimeout(timeoutId);
  }, [vaccineSearch]);

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <ModalHeader closeButton onHide={handleClose}>
        <Modal.Title>Add immunization record</Modal.Title>
      </ModalHeader>
      <Modal.Body>
        {!showManualEntry ? (
          <>
            <p>
              Upload a vaccination record sheet or add immunization manually.
            </p>
            <div
              {...getRootProps()}
              className="p-5 rounded text-center upload-section"
            >
              <input {...getInputProps()} />
              <FontAwesomeIcon icon={faUpload} size="2x" className="mb-3" />
              <p className="mb-0">
                OCR
              </p>
              <small className="text-muted">
                Upload a PDF, image to enter data
              </small>
            </div>
            {files.length > 0 && (
              <div className="mt-3">
                <p>Selected file: {files[0].name}</p>
              </div>
            )}
            <div className="mt-4">
              <div
                className="p-5 rounded text-center manual-entry-section"
                onClick={() => setShowManualEntry(true)}
              >
                <FontAwesomeIcon icon={['far', 'plus']} size="2x" className="mb-3" />
                <p className="mb-0">Add immunization manually</p>
                <small className="text-muted">
                  Manually add new immunizations
                </small>
              </div>
            </div>
          </>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Search Vaccine</Form.Label>
              <Form.Control
                value={vaccineSearch}
                placeholder="Search and select a vaccine"
                onChange={(e) => setVaccineSearch(e.target.value)}
              />
            </Form.Group>

            {isSearching && <div className="text-muted">Loading...</div>}

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {selectedVaccine ? (
                <div
                  className="py-3 pointer border-bottom bg-light"
                  onClick={() => setSelectedVaccine(null)}
                >
                  <h4 className="font-weight-normal">{selectedVaccine.name}</h4>
                  <div className="text-muted">{selectedVaccine.description}</div>
                </div>
              ) : (
                foundVaccines.map((vaccine) => (
                  <div
                    key={vaccine.cvx_code}
                    className="py-3 pointer border-bottom"
                    onClick={() => setSelectedVaccine(vaccine)}
                  >
                    <h4 className="font-weight-normal">{vaccine.name}</h4>
                    <div className="text-muted">{vaccine.description}</div>
                  </div>
                ))
              )}
            </div>

            {selectedVaccine && (
              <Form.Group className="mb-3">
                <Form.Label>Vaccination Date</Form.Label>
                <Form.Control
                  type="date"
                  value={vaccinatedAt}
                  onChange={(e) => setVaccinatedAt(e.target.value)}
                />
              </Form.Group>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!showManualEntry ? (
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSaveVaccine}
            disabled={!selectedVaccine || !vaccinatedAt}
          >
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UploadImmunizationModal;
